/**
 * Default Chart of Accounts Template
 *
 * Structure: Level 1 (1 digit) → Level 2 (2 digits) → Level 3 (3 digits) → Level 4 (5 digits) → Level 5 (8 digits)
 *
 * Level 1-4: System accounts (cannot be deleted/edited)
 * Level 5: User accounts (postable, can be added by user or auto-created for customers/vendors/banks)
 *
 * Account Types:
 * - Asset: Normal balance = Debit
 * - Liability: Normal balance = Credit
 * - Equity: Normal balance = Credit
 * - Revenue: Normal balance = Credit
 * - Expense: Normal balance = Debit
 */

export const DEFAULT_CHART_OF_ACCOUNTS = [
  // ============================================
  // LEVEL 1: ACCOUNT TYPES
  // ============================================
  {
    code: '1',
    name: 'Assets',
    level: 1,
    accountType: 'asset',
    balanceType: 'debit',
    parentId: null,
    isSystemAccount: true,
    isPostable: false,
    description: 'Resources owned by the business'
  },
  {
    code: '2',
    name: 'Liabilities',
    level: 1,
    accountType: 'liability',
    balanceType: 'credit',
    parentId: null,
    isSystemAccount: true,
    isPostable: false,
    description: 'Obligations owed by the business'
  },
  {
    code: '3',
    name: 'Equity',
    level: 1,
    accountType: 'equity',
    balanceType: 'credit',
    parentId: null,
    isSystemAccount: true,
    isPostable: false,
    description: 'Owner\'s interest in the business'
  },
  {
    code: '4',
    name: 'Revenue',
    level: 1,
    accountType: 'revenue',
    balanceType: 'credit',
    parentId: null,
    isSystemAccount: true,
    isPostable: false,
    description: 'Income earned from business operations'
  },
  {
    code: '5',
    name: 'Expenses',
    level: 1,
    accountType: 'expense',
    balanceType: 'debit',
    parentId: null,
    isSystemAccount: true,
    isPostable: false,
    description: 'Costs incurred in business operations'
  },

  // ============================================
  // LEVEL 2: ACCOUNT GROUPS
  // ============================================

  // -- ASSETS (1x) --
  {
    code: '11',
    name: 'Current Assets',
    level: 2,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '1',
    isSystemAccount: true,
    isPostable: false,
    description: 'Assets expected to be converted to cash within one year'
  },
  {
    code: '12',
    name: 'Fixed Assets',
    level: 2,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '1',
    isSystemAccount: true,
    isPostable: false,
    description: 'Long-term tangible assets'
  },
  {
    code: '13',
    name: 'Other Assets',
    level: 2,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '1',
    isSystemAccount: true,
    isPostable: false,
    description: 'Other non-current assets'
  },

  // -- LIABILITIES (2x) --
  {
    code: '21',
    name: 'Current Liabilities',
    level: 2,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '2',
    isSystemAccount: true,
    isPostable: false,
    description: 'Obligations due within one year'
  },
  {
    code: '22',
    name: 'Long Term Liabilities',
    level: 2,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '2',
    isSystemAccount: true,
    isPostable: false,
    description: 'Obligations due after one year'
  },

  // -- EQUITY (3x) --
  {
    code: '31',
    name: 'Capital',
    level: 2,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '3',
    isSystemAccount: true,
    isPostable: false,
    description: 'Owner\'s invested capital'
  },
  {
    code: '32',
    name: 'Retained Earnings',
    level: 2,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '3',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated profits/losses'
  },

  // -- REVENUE (4x) --
  {
    code: '41',
    name: 'Operating Revenue',
    level: 2,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '4',
    isSystemAccount: true,
    isPostable: false,
    description: 'Revenue from core business operations'
  },
  {
    code: '42',
    name: 'Other Income',
    level: 2,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '4',
    isSystemAccount: true,
    isPostable: false,
    description: 'Non-operating income'
  },

  // -- EXPENSES (5x) --
  {
    code: '51',
    name: 'Cost of Sales',
    level: 2,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '5',
    isSystemAccount: true,
    isPostable: false,
    description: 'Direct costs of goods/services sold'
  },
  {
    code: '52',
    name: 'Operating Expenses',
    level: 2,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '5',
    isSystemAccount: true,
    isPostable: false,
    description: 'Day-to-day business expenses'
  },
  {
    code: '53',
    name: 'Financial Expenses',
    level: 2,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '5',
    isSystemAccount: true,
    isPostable: false,
    description: 'Interest and bank charges'
  },

  // ============================================
  // LEVEL 3: ACCOUNT SUB-GROUPS
  // ============================================

  // -- CURRENT ASSETS (11x) --
  {
    code: '111',
    name: 'Cash & Bank',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11',
    isSystemAccount: true,
    isPostable: false,
    description: 'Cash and bank balances'
  },
  {
    code: '112',
    name: 'Receivables',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11',
    isSystemAccount: true,
    isPostable: false,
    description: 'Amounts owed to the business'
  },
  {
    code: '113',
    name: 'Inventory',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11',
    isSystemAccount: true,
    isPostable: false,
    description: 'Stock in trade'
  },
  {
    code: '114',
    name: 'Prepaid Expenses',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11',
    isSystemAccount: true,
    isPostable: false,
    description: 'Expenses paid in advance'
  },

  // -- FIXED ASSETS (12x) --
  {
    code: '121',
    name: 'Property & Equipment',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12',
    isSystemAccount: true,
    isPostable: false,
    description: 'Tangible fixed assets'
  },
  {
    code: '122',
    name: 'Accumulated Depreciation',
    level: 3,
    accountType: 'asset',
    balanceType: 'credit', // Contra asset
    parentCode: '12',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation (contra)'
  },

  // -- OTHER ASSETS (13x) --
  {
    code: '131',
    name: 'Intangible Assets',
    level: 3,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '13',
    isSystemAccount: true,
    isPostable: false,
    description: 'Non-physical assets'
  },

  // -- CURRENT LIABILITIES (21x) --
  {
    code: '211',
    name: 'Payables',
    level: 3,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21',
    isSystemAccount: true,
    isPostable: false,
    description: 'Amounts owed by the business'
  },
  {
    code: '212',
    name: 'Tax Payables',
    level: 3,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21',
    isSystemAccount: true,
    isPostable: false,
    description: 'Taxes owed to government'
  },
  {
    code: '213',
    name: 'Accrued Expenses',
    level: 3,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21',
    isSystemAccount: true,
    isPostable: false,
    description: 'Expenses incurred but not yet paid'
  },

  // -- LONG TERM LIABILITIES (22x) --
  {
    code: '221',
    name: 'Loans',
    level: 3,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '22',
    isSystemAccount: true,
    isPostable: false,
    description: 'Long-term borrowings'
  },

  // -- CAPITAL (31x) --
  {
    code: '311',
    name: 'Owner\'s Capital',
    level: 3,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '31',
    isSystemAccount: true,
    isPostable: false,
    description: 'Capital contributed by owners'
  },

  // -- RETAINED EARNINGS (32x) --
  {
    code: '321',
    name: 'Accumulated Profits',
    level: 3,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '32',
    isSystemAccount: true,
    isPostable: false,
    description: 'Retained earnings from operations'
  },

  // -- OPERATING REVENUE (41x) --
  {
    code: '411',
    name: 'Sales',
    level: 3,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '41',
    isSystemAccount: true,
    isPostable: false,
    description: 'Revenue from sales'
  },
  {
    code: '412',
    name: 'Service Revenue',
    level: 3,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '41',
    isSystemAccount: true,
    isPostable: false,
    description: 'Revenue from services'
  },

  // -- OTHER INCOME (42x) --
  {
    code: '421',
    name: 'Non-Operating Income',
    level: 3,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '42',
    isSystemAccount: true,
    isPostable: false,
    description: 'Income from non-core activities'
  },

  // -- COST OF SALES (51x) --
  {
    code: '511',
    name: 'Cost of Goods Sold',
    level: 3,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51',
    isSystemAccount: true,
    isPostable: false,
    description: 'Direct cost of goods sold'
  },

  // -- OPERATING EXPENSES (52x) --
  {
    code: '521',
    name: 'Administrative Expenses',
    level: 3,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52',
    isSystemAccount: true,
    isPostable: false,
    description: 'General and administrative costs'
  },
  {
    code: '522',
    name: 'Selling Expenses',
    level: 3,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52',
    isSystemAccount: true,
    isPostable: false,
    description: 'Sales and marketing costs'
  },

  // -- FINANCIAL EXPENSES (53x) --
  {
    code: '531',
    name: 'Interest & Bank Charges',
    level: 3,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '53',
    isSystemAccount: true,
    isPostable: false,
    description: 'Financial costs'
  },

  // ============================================
  // LEVEL 4: ACCOUNT CATEGORIES (Parent for Level 5 posting accounts)
  // ============================================

  // -- CASH & BANK (111xx) --
  {
    code: '11101',
    name: 'Cash Accounts',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '111',
    isSystemAccount: true,
    isPostable: false,
    description: 'Cash on hand accounts',
    allowUserAccounts: true // User can add Level 5 accounts here
  },
  {
    code: '11102',
    name: 'Bank Accounts',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '111',
    isSystemAccount: true,
    isPostable: false,
    description: 'Bank account balances',
    linkedEntityType: 'bank', // Banks auto-link here
    allowUserAccounts: true
  },

  // -- RECEIVABLES (112xx) --
  {
    code: '11201',
    name: 'Receivable Customers',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '112',
    isSystemAccount: true,
    isPostable: false,
    description: 'Customer receivables — auto-linked Level-5 accounts per customer',
    linkedEntityType: 'customer', // Customers auto-link here
    allowUserAccounts: false // Only auto-created accounts
  },
  {
    code: '11202',
    name: 'Other Receivables',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '112',
    isSystemAccount: true,
    isPostable: false,
    description: 'Non-trade receivables',
    allowUserAccounts: true
  },
  {
    code: '11203',
    name: 'Vendor Advances',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '112',
    isSystemAccount: true,
    isPostable: false,
    description: 'Advance payments made to vendors before invoice',
    allowUserAccounts: true
  },
  {
    code: '11204',
    name: 'Staff Accounts',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '112',
    isSystemAccount: true,
    isPostable: false,
    description: 'Salary staff swing accounts (advances out, salary owed in, loans, etc.)',
    linkedEntityType: 'staff', // Salary Staff auto-link here
    allowUserAccounts: false   // Only auto-created accounts
  },
  {
    code: '11205',
    name: 'Advance Income Tax',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '112',
    isSystemAccount: true,
    isPostable: false,
    description: 'Withholding / advance income tax deducted by customers on sales (recoverable)',
    allowUserAccounts: true
  },
  {
    code: '11205001',
    name: 'WHT Receivable (Advance Tax)',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11205',
    isSystemAccount: true,
    isPostable: true,
    description: 'Income tax withheld by customers on sale invoices — claimable against tax liability',
    allowUserAccounts: false
  },

  // -- INVENTORY (113xx) --
  {
    code: '11301',
    name: 'Inventory',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '113',
    isSystemAccount: true,
    isPostable: false,
    description: 'Inventory for sale',
    allowUserAccounts: true
  },

  {
    code: '11302',
    name: 'WIP Inventory',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '113',
    isSystemAccount: true,
    isPostable: false,
    description: 'Work-in-progress inventory during production',
    allowUserAccounts: true
  },

  // -- PREPAID EXPENSES (114xx) --
  {
    code: '11401',
    name: 'Prepaid Rent',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '114',
    isSystemAccount: true,
    isPostable: false,
    description: 'Rent paid in advance',
    allowUserAccounts: true
  },
  {
    code: '11402',
    name: 'Prepaid Insurance',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '114',
    isSystemAccount: true,
    isPostable: false,
    description: 'Insurance paid in advance',
    allowUserAccounts: true
  },

  // -- PROPERTY & EQUIPMENT (121xx) --
  {
    code: '12101',
    name: 'Furniture & Fixtures',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Office furniture and fixtures',
    allowUserAccounts: true
  },
  {
    code: '12102',
    name: 'Office Equipment',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Computers, printers, etc.',
    allowUserAccounts: true
  },
  {
    code: '12103',
    name: 'Vehicles',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Company vehicles',
    allowUserAccounts: true
  },
  {
    code: '12104',
    name: 'Building',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Buildings and improvements',
    allowUserAccounts: true
  },
  {
    code: '12105',
    name: 'Land',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Land owned',
    allowUserAccounts: true
  },
  {
    code: '12106',
    name: 'Plant & Machinery',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '121',
    isSystemAccount: true,
    isPostable: false,
    description: 'Manufacturing plant and machinery',
    allowUserAccounts: true
  },

  // -- POSTABLE LEAVES (L5) for asset cost — used by the Fixed Assets module --
  {
    code: '12101001',
    name: 'Furniture & Fixtures',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Furniture & fixtures at cost',
    allowUserAccounts: false
  },
  {
    code: '12102001',
    name: 'Office Equipment',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Office equipment at cost',
    allowUserAccounts: false
  },
  {
    code: '12103001',
    name: 'Vehicles',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12103',
    isSystemAccount: true,
    isPostable: true,
    description: 'Vehicles at cost',
    allowUserAccounts: false
  },
  {
    code: '12104001',
    name: 'Building',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12104',
    isSystemAccount: true,
    isPostable: true,
    description: 'Building at cost',
    allowUserAccounts: false
  },
  {
    code: '12105001',
    name: 'Land',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12105',
    isSystemAccount: true,
    isPostable: true,
    description: 'Land at cost',
    allowUserAccounts: false
  },
  {
    code: '12106001',
    name: 'Plant & Machinery',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '12106',
    isSystemAccount: true,
    isPostable: true,
    description: 'Plant & machinery at cost',
    allowUserAccounts: false
  },

  // -- ACCUMULATED DEPRECIATION (122xx) --
  {
    code: '12201',
    name: 'Accum Dep - Furniture',
    level: 4,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '122',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation - Furniture',
    allowUserAccounts: true
  },
  {
    code: '12202',
    name: 'Accum Dep - Equipment',
    level: 4,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '122',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation - Equipment',
    allowUserAccounts: true
  },
  {
    code: '12203',
    name: 'Accum Dep - Vehicles',
    level: 4,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '122',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation - Vehicles',
    allowUserAccounts: true
  },
  {
    code: '12204',
    name: 'Accum Dep - Building',
    level: 4,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '122',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation - Building',
    allowUserAccounts: true
  },
  {
    code: '12205',
    name: 'Accum Dep - Plant & Machinery',
    level: 4,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '122',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated depreciation - Plant & Machinery',
    allowUserAccounts: true
  },

  // -- POSTABLE LEAVES (L5) for accumulated depreciation — Fixed Assets module --
  {
    code: '12201001',
    name: 'Accum Dep - Furniture',
    level: 5,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '12201',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated depreciation - Furniture & Fixtures',
    allowUserAccounts: false
  },
  {
    code: '12202001',
    name: 'Accum Dep - Equipment',
    level: 5,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '12202',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated depreciation - Office Equipment',
    allowUserAccounts: false
  },
  {
    code: '12203001',
    name: 'Accum Dep - Vehicles',
    level: 5,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '12203',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated depreciation - Vehicles',
    allowUserAccounts: false
  },
  {
    code: '12204001',
    name: 'Accum Dep - Building',
    level: 5,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '12204',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated depreciation - Building',
    allowUserAccounts: false
  },
  {
    code: '12205001',
    name: 'Accum Dep - Plant & Machinery',
    level: 5,
    accountType: 'asset',
    balanceType: 'credit',
    parentCode: '12205',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated depreciation - Plant & Machinery',
    allowUserAccounts: false
  },

  // -- INTANGIBLE ASSETS (131xx) --
  {
    code: '13101',
    name: 'Software & Licenses',
    level: 4,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '131',
    isSystemAccount: true,
    isPostable: false,
    description: 'Software and licenses',
    allowUserAccounts: true
  },

  // -- PAYABLES (211xx) --
  {
    code: '21101',
    name: 'Payable Vendors',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '211',
    isSystemAccount: true,
    isPostable: false,
    description: 'Vendor payables — auto-linked Level-5 accounts per vendor',
    linkedEntityType: 'vendor', // Vendors auto-link here
    allowUserAccounts: false
  },
  {
    code: '21102',
    name: 'Other Payables',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '211',
    isSystemAccount: true,
    isPostable: false,
    description: 'Non-trade payables',
    allowUserAccounts: true
  },
  {
    code: '21103',
    name: 'Customer Advances',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '211',
    isSystemAccount: true,
    isPostable: false,
    description: 'Advance payments received from customers before invoice',
    allowUserAccounts: true
  },

  // -- TAX PAYABLES (212xx) --
  {
    code: '21201',
    name: 'GST Output',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '212',
    isSystemAccount: true,
    isPostable: false,
    description: 'GST collected on sales',
    allowUserAccounts: true
  },
  {
    code: '21202',
    name: 'GST Input',
    level: 4,
    accountType: 'asset', // This is actually an asset (receivable from govt)
    balanceType: 'debit',
    parentCode: '212',
    isSystemAccount: true,
    isPostable: false,
    description: 'GST paid on purchases',
    allowUserAccounts: true
  },
  {
    code: '21203',
    name: 'WHT Payable',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '212',
    isSystemAccount: true,
    isPostable: false,
    description: 'Withholding tax payable',
    allowUserAccounts: true
  },
  {
    code: '21204',
    name: 'Income Tax Payable',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '212',
    isSystemAccount: true,
    isPostable: false,
    description: 'Income tax payable',
    allowUserAccounts: true
  },

  // -- ACCRUED EXPENSES (213xx) --
  {
    code: '21301',
    name: 'Accrued Salaries',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '213',
    isSystemAccount: true,
    isPostable: false,
    description: 'Salaries owed to employees',
    allowUserAccounts: true
  },
  {
    code: '21302',
    name: 'Accrued Utilities',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '213',
    isSystemAccount: true,
    isPostable: false,
    description: 'Utility bills owed',
    allowUserAccounts: true
  },

  // -- LOANS (221xx) --
  {
    code: '22101',
    name: 'Bank Loans',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '221',
    isSystemAccount: true,
    isPostable: false,
    description: 'Long-term bank loans',
    allowUserAccounts: true
  },
  {
    code: '22102',
    name: 'Director Loans',
    level: 4,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '221',
    isSystemAccount: true,
    isPostable: false,
    description: 'Loans from directors/owners',
    allowUserAccounts: true
  },

  // -- OWNER'S CAPITAL (311xx) --
  {
    code: '31101',
    name: 'Capital Account',
    level: 4,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '311',
    isSystemAccount: true,
    isPostable: false,
    description: 'Owner\'s capital contributions',
    allowUserAccounts: true
  },
  {
    code: '31102',
    name: 'Drawings',
    level: 4,
    accountType: 'equity',
    balanceType: 'debit', // Contra equity
    parentCode: '311',
    isSystemAccount: true,
    isPostable: false,
    description: 'Owner\'s withdrawals',
    allowUserAccounts: true
  },

  // -- ACCUMULATED PROFITS (321xx) --
  {
    code: '32101',
    name: 'Retained Earnings',
    level: 4,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '321',
    isSystemAccount: true,
    isPostable: false,
    description: 'Accumulated profits from previous years',
    allowUserAccounts: true
  },
  {
    code: '32102',
    name: 'Opening Balance Equity',
    level: 4,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '321',
    isSystemAccount: true,
    isPostable: false,
    description: 'For initial setup imbalances',
    allowUserAccounts: true
  },

  // -- SALES (411xx) --
  {
    code: '41101',
    name: 'Sales Revenue',
    level: 4,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '411',
    isSystemAccount: true,
    isPostable: false,
    description: 'Revenue from product sales',
    allowUserAccounts: true
  },
  {
    code: '41102',
    name: 'Sales Returns',
    level: 4,
    accountType: 'revenue',
    balanceType: 'debit', // Contra revenue
    parentCode: '411',
    isSystemAccount: true,
    isPostable: false,
    description: 'Returns and allowances',
    allowUserAccounts: true
  },
  {
    code: '41103',
    name: 'Sales Discounts',
    level: 4,
    accountType: 'revenue',
    balanceType: 'debit', // Contra revenue
    parentCode: '411',
    isSystemAccount: true,
    isPostable: false,
    description: 'Discounts given to customers',
    allowUserAccounts: true
  },

  // -- SERVICE REVENUE (412xx) --
  {
    code: '41201',
    name: 'Service Income',
    level: 4,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '412',
    isSystemAccount: true,
    isPostable: false,
    description: 'Revenue from services',
    allowUserAccounts: true
  },

  // -- NON-OPERATING INCOME (421xx) --
  {
    code: '42101',
    name: 'Interest Income',
    level: 4,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '421',
    isSystemAccount: true,
    isPostable: false,
    description: 'Interest earned',
    allowUserAccounts: true
  },
  {
    code: '42102',
    name: 'Other Income',
    level: 4,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '421',
    isSystemAccount: true,
    isPostable: false,
    description: 'Miscellaneous income',
    allowUserAccounts: true
  },
  {
    code: '42103',
    name: 'Gain on Asset Disposal',
    level: 4,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '421',
    isSystemAccount: true,
    isPostable: false,
    description: 'Gain on disposal of fixed assets (proceeds above net book value)',
    allowUserAccounts: true
  },
  {
    code: '42103001',
    name: 'Gain on Asset Disposal',
    level: 5,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '42103',
    isSystemAccount: true,
    isPostable: true,
    description: 'Gain on disposal of fixed assets (proceeds above net book value)',
    allowUserAccounts: false
  },

  // -- COST OF GOODS SOLD (511xx) --
  {
    code: '51101',
    name: 'Cost of Goods Sold',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Direct cost of products sold',
    allowUserAccounts: true
  },
  {
    code: '51102',
    name: 'Stock Adjustments',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Inventory adjustments and write-offs',
    allowUserAccounts: true
  },
  // Purchase Discount — credited by the "Adjustment" column on Cash/Bank
  // Payments (amount a vendor lets us short-pay). Added after many companies
  // already existed and some made their OWN account at 51103 (the next free
  // slot under 511), so `createOnly`: Repair COA creates it only where 51103 is
  // free and leaves any company's existing 51103 (and its children) untouched.
  {
    code: '51103',
    name: 'Purchase Discount',
    level: 4,
    accountType: 'expense',
    balanceType: 'credit', // Contra expense
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Discounts / short-payment adjustments allowed by vendors',
    allowUserAccounts: true,
    createOnly: true,
    createOnlyStrict: true,
    blockedNote: 'Cash/Bank Payment adjustments need account 51103001 — please contact support to set it up.'
  },
  {
    code: '51104',
    name: 'Carriage Inwards',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Transportation / carriage charged on purchase invoices',
    allowUserAccounts: true
  },
  {
    code: '51105',
    name: 'Other Purchase Charges',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Other charges added on purchase invoices',
    allowUserAccounts: true
  },
  {
    code: '51106',
    name: 'Production Wastage',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '511',
    isSystemAccount: true,
    isPostable: false,
    description: 'Raw material wasted / scrapped in manual production',
    allowUserAccounts: true
  },
  {
    code: '51106001',
    name: 'Production Wastage / Scrap',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51106',
    isSystemAccount: true,
    isPostable: true,
    description: 'Cost of raw material wasted in production (written off)'
  },

  // -- ADMINISTRATIVE EXPENSES (521xx) --
  {
    code: '52101',
    name: 'Salaries & Wages',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Employee salaries and wages',
    allowUserAccounts: true
  },
  {
    code: '52101001',
    name: 'Salaries Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Dr on Payroll Run gross — total payroll expense for the period'
  },
  {
    code: '52101002',
    name: 'Bonus & Incentives',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Optional split for bonus/incentive payments separate from regular salary'
  },
  {
    code: '52102',
    name: 'Rent Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Office/warehouse rent',
    allowUserAccounts: true
  },
  {
    code: '52103',
    name: 'Utilities',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Electricity, gas, water',
    allowUserAccounts: true
  },
  {
    code: '52104',
    name: 'Office Supplies',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Office consumables',
    allowUserAccounts: true
  },
  {
    code: '52105',
    name: 'Communication',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Phone, internet, postage',
    allowUserAccounts: true
  },
  {
    code: '52106',
    name: 'Insurance',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Business insurance',
    allowUserAccounts: true
  },
  {
    code: '52107',
    name: 'Repairs & Maintenance',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Maintenance costs',
    allowUserAccounts: true
  },
  {
    code: '52108',
    name: 'Depreciation Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Depreciation of fixed assets',
    allowUserAccounts: true
  },
  {
    code: '52108001',
    name: 'Depreciation Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52108',
    isSystemAccount: true,
    isPostable: true,
    description: 'Depreciation of fixed assets',
    allowUserAccounts: false
  },
  {
    code: '52115',
    name: 'Loss on Asset Disposal',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Loss on disposal of fixed assets (proceeds below net book value)',
    allowUserAccounts: true
  },
  {
    code: '52115001',
    name: 'Loss on Asset Disposal',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52115',
    isSystemAccount: true,
    isPostable: true,
    description: 'Loss on disposal of fixed assets (proceeds below net book value)',
    allowUserAccounts: false
  },
  // Bad Debts — used by the "Adjustment / Bad Debt" column on Bank/Cash Receipts
  // (amounts written off when clearing a customer invoice). Added after many
  // companies already existed, so `createOnly`: Repair COA only creates these
  // when the code is FREE and never takes over an account a company already
  // made at the same code (see repairChartOfAccounts).
  {
    code: '52116',
    name: 'Bad Debts',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Customer balances written off as uncollectible / settlement adjustments',
    allowUserAccounts: true,
    createOnly: true
  },
  {
    code: '52116001',
    name: 'Bad Debts',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52116',
    isSystemAccount: true,
    isPostable: true,
    description: 'Customer balances written off as uncollectible / settlement adjustments',
    allowUserAccounts: false,
    createOnly: true
  },
  {
    code: '52109',
    name: 'Professional Fees',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Legal, audit, consulting fees',
    allowUserAccounts: true
  },
  {
    code: '52110',
    name: 'Travel & Entertainment',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Business travel and entertainment',
    allowUserAccounts: true
  },
  {
    code: '52111',
    name: 'Office Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'General office running expenses (supplies, snacks, sundries)',
    allowUserAccounts: true
  },
  {
    code: '52111001',
    name: 'Office Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52111',
    isSystemAccount: true,
    isPostable: true,
    description: 'Default postable office-expense account'
  },
  {
    code: '52112',
    name: 'Entertainment Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Client / staff entertainment, meals, hospitality',
    allowUserAccounts: true
  },
  {
    code: '52112001',
    name: 'Entertainment Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52112',
    isSystemAccount: true,
    isPostable: true,
    description: 'Default postable entertainment-expense account'
  },
  {
    code: '52113',
    name: 'Owner Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Owner-related expenses booked through the business',
    allowUserAccounts: true
  },
  {
    code: '52113001',
    name: 'Owner Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52113',
    isSystemAccount: true,
    isPostable: true,
    description: 'Default postable owner-expense account'
  },
  {
    code: '52114',
    name: 'Other Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '521',
    isSystemAccount: true,
    isPostable: false,
    description: 'Miscellaneous operating expenses that do not fit elsewhere',
    allowUserAccounts: true
  },
  {
    code: '52114001',
    name: 'Other Expense',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '52114',
    isSystemAccount: true,
    isPostable: true,
    description: 'Default postable misc-expense account'
  },

  // -- SELLING EXPENSES (522xx) --
  {
    code: '52201',
    name: 'Marketing & Advertising',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '522',
    isSystemAccount: true,
    isPostable: false,
    description: 'Marketing and advertising costs',
    allowUserAccounts: true
  },
  {
    code: '52202',
    name: 'Sales Commission',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '522',
    isSystemAccount: true,
    isPostable: false,
    description: 'Sales commissions paid',
    allowUserAccounts: true
  },
  {
    code: '52203',
    name: 'Delivery & Freight',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '522',
    isSystemAccount: true,
    isPostable: false,
    description: 'Shipping and delivery costs',
    allowUserAccounts: true
  },

  // -- INTEREST & BANK CHARGES (531xx) --
  {
    code: '53101',
    name: 'Bank Charges',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '531',
    isSystemAccount: true,
    isPostable: false,
    description: 'Bank fees and charges',
    allowUserAccounts: true
  },
  {
    code: '53102',
    name: 'Interest Expense',
    level: 4,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '531',
    isSystemAccount: true,
    isPostable: false,
    description: 'Interest on loans',
    allowUserAccounts: true
  },

  // ============================================
  // LEVEL 5: DEFAULT POSTABLE ACCOUNTS
  // (These are common accounts most companies need)
  // ============================================
  {
    code: '11101001',
    name: 'Cash in Hand',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11101',
    isSystemAccount: true, // This one is system account, cannot delete
    isPostable: true,
    description: 'Main cash account'
  },
  {
    code: '11101002',
    name: 'Petty Cash',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Petty cash fund'
  },
  {
    code: '11102001',
    name: 'Primary Bank Account',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Main bank account'
  },
  {
    code: '11301001',
    name: 'Inventory Control',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11301',
    isSystemAccount: true,
    isPostable: true,
    description: 'Main inventory control account'
  },
  {
    code: '11302001',
    name: 'WIP - Production',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '11302',
    isSystemAccount: true,
    isPostable: true,
    description: 'Work-in-progress inventory for production orders'
  },
  {
    code: '21201001',
    name: 'GST Output - Sales',
    level: 5,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21201',
    isSystemAccount: true,
    isPostable: true,
    description: 'GST collected on sales'
  },
  {
    code: '21202001',
    name: 'GST Input - Purchases',
    level: 5,
    accountType: 'asset',
    balanceType: 'debit',
    parentCode: '21202',
    isSystemAccount: true,
    isPostable: true,
    description: 'GST paid on purchases'
  },
  {
    code: '21203001',
    name: 'WHT Payable - Services',
    level: 5,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21203',
    isSystemAccount: true,
    isPostable: true,
    description: 'WHT on services'
  },
  {
    code: '21203002',
    name: 'WHT Payable - Supplies',
    level: 5,
    accountType: 'liability',
    balanceType: 'credit',
    parentCode: '21203',
    isSystemAccount: true,
    isPostable: true,
    description: 'WHT on supplies'
  },
  {
    code: '31101001',
    name: 'Owner\'s Capital',
    level: 5,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '31101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Main capital account'
  },
  {
    code: '31102001',
    name: 'Owner\'s Drawings',
    level: 5,
    accountType: 'equity',
    balanceType: 'debit',
    parentCode: '31102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Main drawings account'
  },
  {
    code: '32101001',
    name: 'Retained Earnings',
    level: 5,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '32101',
    isSystemAccount: true,
    isPostable: true,
    description: 'Accumulated retained earnings'
  },
  {
    code: '32102001',
    name: 'Opening Balance Equity',
    level: 5,
    accountType: 'equity',
    balanceType: 'credit',
    parentCode: '32102',
    isSystemAccount: true,
    isPostable: true,
    description: 'For initial setup imbalances'
  },
  {
    code: '41101001',
    name: 'Sales - General',
    level: 5,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '41101',
    isSystemAccount: true,
    isPostable: true,
    description: 'General sales revenue'
  },
  {
    code: '41102001',
    name: 'Sales Returns - General',
    level: 5,
    accountType: 'revenue',
    balanceType: 'debit',
    parentCode: '41102',
    isSystemAccount: true,
    isPostable: true,
    description: 'General sales returns'
  },
  {
    code: '51101001',
    name: 'Cost of Goods Sold - General',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51101',
    isSystemAccount: true,
    isPostable: true,
    description: 'General COGS'
  },
  {
    code: '51102001',
    name: 'Stock Adjustment',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Inventory adjustments and write-offs'
  },
  {
    code: '51103001',
    name: 'Purchase Discount',
    level: 5,
    accountType: 'expense',
    balanceType: 'credit', // Contra expense
    parentCode: '51103',
    isSystemAccount: true,
    isPostable: true,
    description: 'Discounts / short-payment adjustments allowed by vendors',
    allowUserAccounts: false,
    createOnly: true,
    createOnlyStrict: true,
    blockedNote: 'Cash/Bank Payment adjustments need account 51103001 — please contact support to set it up.'
  },
  {
    code: '53101001',
    name: 'Bank Charges - General',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '53101',
    isSystemAccount: true,
    isPostable: true,
    description: 'General bank charges'
  },
  // Charges recovered from customers on Sale Invoices (non-taxable add-ons at
  // totals level). Kept under Other Income (42102) so they stay OUT of the
  // taxable Sales Revenue figure used for GST reconciliation.
  {
    code: '42102001',
    name: 'Carriage / Freight Recovered',
    level: 5,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '42102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Transportation / carriage charges recovered from customers on sale invoices'
  },
  {
    code: '42102002',
    name: 'Other Charges Recovered',
    level: 5,
    accountType: 'revenue',
    balanceType: 'credit',
    parentCode: '42102',
    isSystemAccount: true,
    isPostable: true,
    description: 'Other charges recovered from customers on sale invoices'
  },
  // Charges paid to vendors on Purchase Invoices (non-taxable add-ons at totals
  // level). Expensed under Cost of Sales (511) — carriage inwards is part of the
  // cost of acquiring goods.
  {
    code: '51104001',
    name: 'Carriage / Freight Inwards',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51104',
    isSystemAccount: true,
    isPostable: true,
    description: 'Transportation / carriage charged by vendors on purchase invoices'
  },
  {
    code: '51105001',
    name: 'Other Charges (Purchase)',
    level: 5,
    accountType: 'expense',
    balanceType: 'debit',
    parentCode: '51105',
    isSystemAccount: true,
    isPostable: true,
    description: 'Other charges added by vendors on purchase invoices'
  }
];

/**
 * Parent account codes for linked entities.
 * Used by coaInitializer.ensureParentAccountExists when auto-creating Level-5
 * accounts on entity save (customer/vendor/bank/staff).
 */
export const LINKED_ACCOUNT_PARENTS = {
  customer: '11201', // Receivable Customers
  vendor: '21101',   // Payable Vendors
  bank: '11102',     // Bank Accounts
  staff: '11204'     // Staff Accounts
};

/**
 * Get parent account info for an entity type
 */
export const getParentAccountForEntity = (entityType) => {
  const parentCode = LINKED_ACCOUNT_PARENTS[entityType];
  if (!parentCode) return null;

  return DEFAULT_CHART_OF_ACCOUNTS.find(a => a.code === parentCode);
};

/**
 * Generate next account code for a Level 5 account under a parent
 * @param {string} parentCode - Parent account code (Level 4)
 * @param {number} existingCount - Number of existing Level 5 accounts under this parent
 * @returns {string} - Next account code (8 digits)
 */
export const generateLevel5Code = (parentCode, existingCount) => {
  const nextNumber = existingCount + 1;
  return `${parentCode}${String(nextNumber).padStart(3, '0')}`;
};

export default DEFAULT_CHART_OF_ACCOUNTS;
